import type { FieldConfig, FieldValue, FieldValues, TableRowValue, TableFieldConfig } from '@shared/types/interactiveFields';

export function useFieldValidation() {
  const validateField = (field: FieldConfig, value: FieldValue): string | null => {
    // Required check
    if (field.required) {
      if (value === undefined || value === null || value === '') {
        return `${field.label.bg} е задължително`;
      }

      // Table: check min rows
      if (field.type === 'table') {
        const rows = value as TableRowValue[];
        const tableField = field as TableFieldConfig;
        const minRows = tableField.minRows || 1;
        if (!rows || rows.length < minRows) {
          return `Добавете поне ${minRows} ${minRows === 1 ? 'ред' : 'реда'}`;
        }

        // Check required columns
        for (const row of rows) {
          for (const col of tableField.columns) {
            if (col.required && !row.values[col.id]) {
              return `${col.header.bg} е задължително за всеки ред`;
            }
          }
        }
      }

      // Checkbox: must be checked if required
      if (field.type === 'checkbox' && !value) {
        return `${field.label.bg} е задължително`;
      }
    }

    // Type-specific validation
    if (field.type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value as string)) {
        return 'Невалиден email адрес';
      }
    }

    if (field.type === 'tel' && value) {
      const phoneRegex = /^[\d\s+()-]+$/;
      if (!phoneRegex.test(value as string)) {
        return 'Невалиден телефонен номер';
      }
    }

    // Text length validation
    if ((field.type === 'text' || field.type === 'textarea') && value) {
      const strValue = value as string;

      if ('minLength' in field && field.minLength && strValue.length < field.minLength) {
        return `Минимум ${field.minLength} символа`;
      }
      if ('maxLength' in field && field.maxLength && strValue.length > field.maxLength) {
        return `Максимум ${field.maxLength} символа`;
      }
    }

    return null;
  };

  const validateAll = (fields: FieldConfig[], values: FieldValues): Record<string, string> => {
    const errors: Record<string, string> = {};

    for (const field of fields) {
      const error = validateField(field, values[field.id]);
      if (error) {
        errors[field.id] = error;
      }
    }

    return errors;
  };

  const isValid = (fields: FieldConfig[], values: FieldValues): boolean => {
    return Object.keys(validateAll(fields, values)).length === 0;
  };

  return { validateField, validateAll, isValid };
}
